import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { assimagraLogoWhite, authBackground } from "@/assets";
import { LanguageToggle } from "../components/LanguageToggle";
import FirestoreService from "../services/firestore";
import { authService } from "../services/auth/authService";
import { Progress } from "../components/ui/progress";
import { AlertCircle, CheckCircle, Info, EyeOff, Eye, ShieldCheck } from "lucide-react";

const SetPassword = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [strength, setStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState({ password: false, confirmPassword: false });
  const [linkError, setLinkError] = useState<string | null>(null);
  const [isValidatingInvitation, setIsValidatingInvitation] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const emailParam = params.get("email");

    if (!token || !emailParam) {
      setLinkError(t("auth.setPassword.error.invalidLink"));
      return;
    }

    setEmail(emailParam);
    
    // Validate the invitation
    const validateInvitation = async () => {
      setIsValidatingInvitation(true);
      try {
        const validation = await authService.validateInvitation(emailParam);
        if (!validation.isValid) {
          let errorMessage = t("auth.setPassword.error.invalidLink");
          
          switch (validation.error) {
            case "invitation_not_found":
              errorMessage = t("auth.setPassword.error.invitationNotFound");
              break;
            case "invitation_expired":
              errorMessage = t("auth.setPassword.error.expiredLink");
              break;
            case "invitation_used":
              errorMessage = t("auth.setPassword.error.invitationUsed");
              break;
            case "invitation_superseded":
              errorMessage = t("auth.setPassword.error.invitationSuperseded");
              break;
            case "invitation_revoked":
              errorMessage = t("auth.setPassword.error.invitationRevoked");
              break;
          }
          
          setLinkError(errorMessage);
        }
      } catch (error) {
        console.error("Error validating invitation:", error);
        setLinkError(t("auth.setPassword.error.invalidLink"));
      } finally {
        setIsValidatingInvitation(false);
      }
    };

    validateInvitation();
  }, [location, t]);

  useEffect(() => {
    // Calculate password strength with more factors for UX, even though we only require 6 chars
    let score = 0;

    if (password.length > 0) {
      // Length check - minimum requirement
      if (password.length < 6) {
        score = Math.min(30, password.length * 5); // Some progress for typing, max 30%
      } else {
        // Base score for minimum length
        score = 60;

        // Bonus points for better security (but not required)
        if (password.length > 8) score += 10;
        if (/[A-Z]/.test(password)) score += 10;
        if (/[0-9]/.test(password)) score += 10;
        if (/[^A-Za-z0-9]/.test(password)) score += 10;
      }
    }

    setStrength(score);
  }, [password, confirmPassword, t]);

  const validatePasswords = (): boolean => {
    // Even with the enhanced strength calculation, we still only require 6 chars minimum
    return password.length >= 6 && password === confirmPassword;
  };

  const getStrengthColor = () => {
    if (strength < 40) return "bg-red-600";
    if (strength < 80) return "bg-yellow-500";
    return "bg-teal-600";
  };

  const getStrengthLabel = () => {
    if (strength === 0) return "";
    if (strength < 40) return t("auth.setPassword.strength.weak");
    if (strength < 80) return t("auth.setPassword.strength.medium");
    return t("auth.setPassword.strength.strong");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Set fields as touched
    setTouched({ password: true, confirmPassword: true });

    // Validate passwords before submitting
    if (!validatePasswords()) {
      // Show toast notification for password validation errors
      if (password.length < 6) {
        toast({
          variant: "destructive",
          title: t("error"),
          description: t("auth.setPassword.feedback.passwordTooShort"),
        });
        return;
      }

      if (password !== confirmPassword) {
        toast({
          variant: "destructive",
          title: t("error"),
          description: t("auth.setPassword.feedback.passwordMismatch"),
        });
        return;
      }

      return;
    }

    setIsSubmitting(true);

    try {
      const params = new URLSearchParams(location.search);
      const token = params.get("token");

      await FirestoreService.call("validateTokenAndCreateUser", {
        token,
        email,
        password
      });

      // Mark the invitation as used after successful account creation
      await authService.markInvitationAsUsed(email);

      toast({
        title: t("success"),
        description: t("auth.setPassword.feedback.success"),
      });

      navigate("/login");

    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: error.message || t("auth.setPassword.feedback.error"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Language toggle - fixed position */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageToggle />
      </div>

      {/* Left side with background image and logo */}
      <div
        className="hidden [@media(min-width:1200px)]:flex [@media(min-width:1200px)]:w-1/2 relative p-8 flex-col justify-between overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url("${authBackground}")`,
          backgroundSize: "120% auto",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="flex flex-col text-white">
          <img src={assimagraLogoWhite} alt="ASSIMAGRA" className="w-80 h-auto mb-12 brightness-[1.15] saturate-0" />
          <h1 className="text-4xl font-bold leading-tight" style={{ textShadow: "3px 4px 8px rgba(0, 0, 0, 0.4)" }}>
            {t("auth.setPassword.pageTitle.line1")}
            <br />
            {t("auth.setPassword.pageTitle.line2")}
            <br />
            {t("auth.setPassword.pageTitle.line3")}
          </h1>
        </div>
      </div>

      {/* Right side with password form */}
      <div className="w-full [@media(min-width:1200px)]:w-1/2 p-8 flex flex-col">
        <div className="flex-grow flex items-center justify-center">
          <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-xl font-semibold">{t("auth.setPassword.title")}</h2>
            </div>
            <p className="text-gray-600 mb-5 text-sm">{t("auth.setPassword.subtitle")}</p>

            {isValidatingInvitation ? (
              <div className="p-4 border border-blue-200 bg-blue-50 rounded-md text-blue-700 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  <h3 className="font-medium">{t("auth.setPassword.validating")}</h3>
                </div>
                <p>{t("auth.setPassword.validatingMessage")}</p>
              </div>
            ) : linkError ? (
              <div className="p-4 border border-red-200 bg-red-50 rounded-md text-red-700 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-5 w-5" />
                  <h3 className="font-medium">{t("error")}</h3>
                </div>
                <p>{linkError}</p>
                {(linkError === t("auth.setPassword.error.expiredLink") ||
                  linkError === t("auth.setPassword.error.invalidActionCode")) && (
                  <p className="mt-2 text-sm">{t("auth.setPassword.error.linkExplanation")}</p>
                )}
                <Button
                  className="mt-4 w-full bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={() => navigate("/login")}
                >
                  {t("auth.setPassword.button.backToLogin")}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("auth.setPassword.email")}</label>
                  <Input type="email" value={email} disabled className="bg-gray-100" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("auth.setPassword.password")}
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={() => setTouched({ ...touched, password: true })}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  <div className="mt-2">
                    {password && password.length > 0 && (
                      <div className="flex justify-between items-center mb-1">
                        <div className="flex items-center gap-1">
                          <Info className="h-3 w-3 text-gray-500" />
                          <span className={`text-xs ${password.length >= 6 ? "text-teal-600" : "text-gray-600"}`}>
                            {t("auth.setPassword.feedback.requireLength")}
                          </span>
                        </div>
                        {password.length > 0 && (
                          <span
                            className={`text-xs font-medium ${
                              strength < 40 ? "text-red-600" : strength < 80 ? "text-yellow-600" : "text-teal-600"
                            }`}
                          >
                            {getStrengthLabel()}
                          </span>
                        )}
                      </div>
                    )}
                    <Progress
                      value={strength}
                      className="h-1.5 rounded-full overflow-hidden"
                      indicatorClassName={getStrengthColor()}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("auth.setPassword.confirmPassword")}
                  </label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onBlur={() => setTouched({ ...touched, confirmPassword: true })}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  {/* Show password mismatch error when fields are touched and don't match */}
                  {(touched.confirmPassword || confirmPassword) && (
                    <div className="mt-1">
                      {password === confirmPassword ? (
                        <div className="flex items-center gap-1 text-xs text-teal-600">
                          <CheckCircle className="h-3 w-3" />
                          <span>{t("auth.setPassword.feedback.passwordsMatch")}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-xs text-red-600">
                          <AlertCircle className="h-3 w-3" />
                          <span>{t("auth.setPassword.feedback.passwordMismatch")}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full mt-2 flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white"
                  disabled={isSubmitting || !validatePasswords()}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      {t("auth.setPassword.button.loading")}
                    </span>
                  ) : (
                    <>
                      <ShieldCheck className="h-4 w-4" />
                      {t("auth.setPassword.button.default")}
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
